/**
 * useFirebaseChat — real-time Firestore chat subscription.
 *
 * Signs into Firebase with the custom token the FinminOS backend mints
 * (stored in sessionStorage via @finmin/auth TokenStore) then subscribes
 * to the `chats/{requestId}/messages` collection ordered by timestamp.
 *
 * Keeps all Firebase concerns here so UI components stay clean.
 */
import * as React from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import {
  collection,
  addDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  limit,
} from 'firebase/firestore';
import { firebaseAuth, firestoreDb } from '@/lib/firebase';
import { tokenStore } from '@/lib/api';

/** 'text' (default), 'document_request' (provider/admin asks for a file), or
 *  'document' (a file was uploaded and shared into the chat). */
export type ChatMessageType = 'text' | 'document_request' | 'document';

export interface ChatMessage {
  id:            string;
  text:          string;
  senderRole:    string;
  senderId:      string;
  timestamp:     Date | null;
  messageType:   ChatMessageType;
  /** For 'document' messages — the uploaded document's id (for download). */
  documentId:    string | null;
  /** Human label — the requested doc name, or the shared file's display name. */
  documentLabel: string | null;
  /** For 'document' messages — the original file name. */
  fileName:      string | null;
}

export type ChatStatus = 'idle' | 'connecting' | 'ready' | 'error';

/** Coerce an arbitrary Firestore value to one of our known message types. */
function normaliseType(value: unknown): ChatMessageType {
  return value === 'document_request' || value === 'document' ? value : 'text';
}

/** First non-empty string among the candidates, else null. */
function firstString(...values: unknown[]): string | null {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v;
  }
  return null;
}

interface UseFirebaseChatOptions {
  requestId:  string | null | undefined;
  customerId: string;
}

export function useFirebaseChat({ requestId, customerId }: UseFirebaseChatOptions) {
  const [messages,   setMessages]   = React.useState<ChatMessage[]>([]);
  const [status,     setStatus]     = React.useState<ChatStatus>('idle');
  const [error,      setError]      = React.useState<string | null>(null);
  const [sending,    setSending]    = React.useState(false);

  // Connect and subscribe when requestId is available
  React.useEffect(() => {
    if (!requestId) return;

    const firestoreToken = tokenStore.getFirestoreToken();
    if (!firestoreToken) {
      setError('Chat is not available yet. Please refresh the page.');
      return;
    }

    let mounted    = true;
    let unsubscribe: (() => void) | null = null;

    setStatus('connecting');
    setError(null);

    (async () => {
      try {
        await signInWithCustomToken(firebaseAuth, firestoreToken);
        if (!mounted) return;

        const messagesRef = collection(firestoreDb, 'chats', requestId, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'asc'), limit(300));

        unsubscribe = onSnapshot(
          q,
          snapshot => {
            if (!mounted) return;
            // Matches old FinMin_User_Dashboard exactly: read every message
            // from chats/{requestId}/messages, no client-side filtering.
            // Privacy is handled by Firestore security rules + the admin
            // token's role claim at the database layer.
            setMessages(
              snapshot.docs.map(doc => {
                const d = doc.data() ?? {};
                return {
                  id:            doc.id,
                  text:          String(d.text ?? ''),
                  senderRole:    String(d.sender_role ?? d.senderRole ?? ''),
                  senderId:      String(d.sender_id   ?? d.senderId   ?? ''),
                  timestamp:     d.timestamp?.toDate?.() ?? null,
                  messageType:   normaliseType(d.message_type ?? d.messageType),
                  documentId:    firstString(d.document_id, d.documentId),
                  documentLabel: firstString(d.document_label, d.documentLabel),
                  fileName:      firstString(d.file_name, d.fileName),
                };
              }),
            );
            setStatus('ready');
          },
          _err => {
            if (!mounted) return;
            setError('Chat is coming soon after provider assignment.');
            setStatus('error');
          },
        );
      } catch {
        if (!mounted) return;
        setError('Chat is coming soon after provider assignment.');
        setStatus('error');
      }
    })();

    return () => {
      mounted = false;
      unsubscribe?.();
    };
  }, [requestId]);   // intentionally excludes firestoreToken — it's stable per session

  async function sendMessage(text: string): Promise<boolean> {
    const trimmed = text.trim();
    if (!trimmed || !requestId || sending) return false;

    setSending(true);
    try {
      const messagesRef = collection(firestoreDb, 'chats', requestId, 'messages');
      await addDoc(messagesRef, {
        text:         trimmed,
        message_type: 'text',
        sender_role:  'customer',
        sender_id:    customerId,
        timestamp:    serverTimestamp(),
      });
      return true;
    } catch {
      return false;
    } finally {
      setSending(false);
    }
  }

  /**
   * Share an already-uploaded document into the chat. The file itself is
   * uploaded via the documents REST API first; this only writes the Firestore
   * message that surfaces it (with a download affordance) to provider/admin.
   */
  async function sendDocumentMessage(payload: {
    documentId: string;
    fileName:   string;
    label?:     string;
  }): Promise<boolean> {
    if (!requestId || sending) return false;
    setSending(true);
    try {
      const label = payload.label?.trim() || payload.fileName;
      const messagesRef = collection(firestoreDb, 'chats', requestId, 'messages');
      await addDoc(messagesRef, {
        text:           label,
        message_type:   'document',
        document_id:    payload.documentId,
        document_label: label,
        file_name:      payload.fileName,
        sender_role:    'customer',
        sender_id:      customerId,
        timestamp:      serverTimestamp(),
      });
      return true;
    } catch {
      return false;
    } finally {
      setSending(false);
    }
  }

  return { messages, status, error, sending, sendMessage, sendDocumentMessage };
}
